import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { customersApi, billingApi, integrationsApi, billingRunsApi, haloApi, BillingRunInfo } from '../services/api';
import FlagDialog, { FlagDialogMode } from '../components/FlagDialog';
import './Billing.css';

interface ServiceSummary {
  service_type: string;
  mapping_count: number;
  product_count: number;
  total_quantity: number;
  has_products: boolean;
}

interface CustomerBillingItem {
  customer_id: number;
  customer_name: string;
  xero_customer_id: string | null;
  contact_email: string | null;
  status: string;
  has_xero: boolean;
  has_microsoft365: boolean;
  has_cove: boolean;
  has_cloudblue: boolean;
  has_sentinelone: boolean;
  has_whmcs: boolean;
  has_splynx: boolean;
  has_portaone?: boolean;
  has_redstor?: boolean;
  has_manual: boolean;
  total_mappings: number;
  total_products: number;
  total_quantity: number;
  monthly_total: string;
  priced_product_count: number;
  unpriced_product_count: number;
  xero_linked_count: number;
  xero_unlinked_count: number;
  m365_check: string;
  m365_total: number;
  cloudblue_m365_total: number;
  m365_mismatch_lines: number;
  services: ServiceSummary[];
  billing_status: string;
  billing_entity?: 'cloudits' | 'webcafe' | null;
  review_flag?: boolean;
  review_note?: string | null;
  review_flagged_by?: string | null;
  review_flagged_at?: string | null;
  hidden?: boolean;                 // shared "hidden from Invoice Prep" (PUT /customers/{id}/hide)
  billing_note?: string | null;     // persistent standing instructions (PUT /customers/{id}/note)
  billing_note_by?: string | null;
  billing_note_at?: string | null;
  processed?: boolean;
  processed_by?: string | null;
  processed_at?: string | null;
  processed_note?: string | null;
  missing_services: string[];
  last_synced: string | null;
  // snapshot mode only (from /billing/runs/{id}/overview)
  invoice_status?: 'pending' | 'invoiced';
  invoiced_at?: string | null;
  invoiced_by?: string | null;
  invoice_ref?: string | null;
}

type SortKey = 'status' | 'customer' | 'services' | 'm365' | 'products' | 'quantity' | 'amount' | 'processed' | 'actions';

interface BillingOverview {
  total_customers: number;
  ready_to_bill: number;
  incomplete: number;
  unmapped: number;
  total_m365_licenses: number;
  total_cove_devices: number;
  grand_total: string;
  ready_total: string;
  customers_needing_pricing: number;
  m365_mismatches: number;
  processed_count?: number;
  period: string | null;
  customers: CustomerBillingItem[];
  last_m365_sync: string | null;
  last_cove_sync: string | null;
  last_xero_sync: string | null;
  // snapshot mode only
  run?: BillingRunInfo;
  invoiced_count?: number;
  invoiced_total?: string;
  outstanding_count?: number;
  outstanding_total?: string;
}

const periodLabel = (period: string) => {
  const d = new Date(`${period}-01T00:00:00`);
  return isNaN(d.getTime()) ? period : d.toLocaleString('en-ZA', { month: 'long', year: 'numeric' });
};

const SYNC_ICON: Record<string, string> = { success: '✅', partial: '⚠️', error: '❌', skipped: '⏭', running: '⏳' };

// ── Halo push (snapshot mode) ──
interface HaloRunCustomer {
  item_id: number; customer_id: number; customer_name: string | null; status: string; delta: string;
  exceptions: { code: string; detail: string }[]; warnings: string[]; ri_ids: number[];
  pushed_at: string | null; message: string | null;
}
interface HaloRunSummary {
  fire_at: string | null; hours_left: number | null; allowlist_mode: 'off' | 'list' | 'all';
  counts: Record<string, number>; customers: HaloRunCustomer[];
}
const HALO_CHIP: Record<string, { icon: string; label: string; cls: string }> = {
  ready: { icon: '⬆', label: 'Ready', cls: 'ready' },
  no_change: { icon: '=', label: 'No change', cls: 'nochange' },
  blocked: { icon: '⛔', label: 'Blocked', cls: 'blocked' },
  not_mapped: { icon: '—', label: 'Not mapped', cls: 'notmapped' },
  already_fired: { icon: '⚠', label: 'Already fired', cls: 'fired' },
  pushing: { icon: '⏳', label: 'Pushing…', cls: 'busy' },
  verifying: { icon: '⏳', label: 'Verifying…', cls: 'busy' },
  pushed: { icon: '✓', label: 'Pushed', cls: 'pushed' },
  verify_failed: { icon: '✗', label: 'Verify failed', cls: 'failed' },
  error: { icon: '✗', label: 'Error', cls: 'failed' },
  rolled_back: { icon: '↩', label: 'Rolled back', cls: 'nochange' },
};
const signedZAR = (v: string) => {
  const n = parseFloat(v || '0');
  const abs = `R ${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n > 0 ? `+${abs}` : n < 0 ? `-${abs}` : abs;
};
const formatDateTime = (s: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function HaloChip({ row, onOpen, onMapping }: { row?: HaloRunCustomer; onOpen: () => void; onMapping: () => void }) {
  if (!row) return <span className="halo-chip notmapped" title="Not part of this run">—</span>;
  const m = HALO_CHIP[row.status] || HALO_CHIP.error;
  if (row.status === 'not_mapped') {
    return <button className="halo-chip notmapped" onClick={onMapping} title="No confirmed Halo recurring invoice - map it on the HaloPSA page">{m.icon} {m.label}</button>;
  }
  const detail = row.status === 'ready' || row.status === 'already_fired' ? ` ${signedZAR(row.delta)}`
    : row.status === 'blocked' ? ` (${row.exceptions.length})` : '';
  const title = [row.message, ...row.exceptions.map(e => e.detail), ...row.warnings].filter(Boolean).join('\n') || m.label;
  return <button className={`halo-chip ${m.cls}`} onClick={onOpen} title={title}>{m.icon} {m.label}{detail}</button>;
}

function HaloPushDrawer({ runId, mode, customerId, summary, onClose, onChanged }: {
  runId: number; mode: 'customer' | 'all'; customerId?: number; summary: HaloRunSummary;
  onClose: () => void; onChanged: () => Promise<void>;
}) {
  const [detail, setDetail] = useState<any>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<{ customer_id: number; status: string; message: string | null }[] | null>(null);
  const row = mode === 'customer' ? summary.customers.find(c => c.customer_id === customerId) : undefined;
  const readyRows = summary.customers.filter(c => c.status === 'ready');

  useEffect(() => {
    if (mode !== 'customer' || !customerId) return;
    haloApi.runCustomer(runId, customerId).then(setDetail).catch((e: any) => toast.error(e.response?.data?.detail || 'Failed to load the Halo diff'));
  }, [runId, mode, customerId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const push = async () => {
    const n = mode === 'customer' ? 1 : readyRows.length;
    const who = n === 1 ? (row?.customer_name || 'this customer') : `${n} customers`;
    if (!window.confirm(`Push ${who} to HaloPSA now?\n\nThe recurring invoice lines are replaced with the snapshot quantities at the current price-book prices, then re-read and verified. A verified push ticks the customer processed for the month.`)) return;
    setBusy(true);
    try {
      const r = await haloApi.runPush(runId, mode === 'customer' ? { customer_ids: [customerId!] } : { all_ready: true });
      setResults(r.results);
      const ok = r.results.filter((x: any) => x.status === 'pushed').length;
      if (ok === r.results.length) toast.success(`${ok} customer${ok === 1 ? '' : 's'} pushed and verified`);
      else toast.error(`${ok} of ${r.results.length} pushed - see the results`);
      await onChanged();
      if (mode === 'customer' && customerId) setDetail(await haloApi.runCustomer(runId, customerId));
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Push failed');
    } finally { setBusy(false); }
  };
  const rollback = async () => {
    if (!detail || !window.confirm(`Roll back ${row?.customer_name || 'this customer'}?\n\nThe recurring invoice lines are restored to what they were before the push and the customer goes back to "to invoice".`)) return;
    setBusy(true);
    try {
      await haloApi.runRollback(runId, detail.item_id);
      toast.success('Rolled back');
      await onChanged();
      setDetail(await haloApi.runCustomer(runId, customerId!));
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Rollback failed');
    } finally { setBusy(false); }
  };

  const status = detail?.status || row?.status;
  const canPush = mode === 'all' ? readyRows.length > 0 : (status === 'ready' || status === 'already_fired');
  const canRollback = mode === 'customer' && (status === 'pushed' || status === 'verify_failed');

  return (
    <>
      <div className="halo-backdrop" onClick={onClose} />
      <aside className="halo-drawer" role="dialog" aria-label="Push to Halo">
        <header>
          <h2>⬆ Push to Halo — {mode === 'customer' ? (row?.customer_name || `customer ${customerId}`) : `${readyRows.length} customer${readyRows.length === 1 ? '' : 's'} ready`}</h2>
          <button className="link-btn" onClick={onClose}>✕</button>
        </header>
        {summary.fire_at && <p className="halo-fire">🔥 Halo generates the invoices {formatDateTime(summary.fire_at)}{summary.hours_left != null ? ` · ${summary.hours_left} h left` : ''}</p>}

        {mode === 'all' && !results && (
          <ul className="halo-ready-list">
            {readyRows.map(c => <li key={c.customer_id}><span>{c.customer_name}</span><span className="halo-delta">{signedZAR(c.delta)}</span></li>)}
            {readyRows.length === 0 && <li className="halo-muted">Nothing is ready to push.</li>}
          </ul>
        )}

        {mode === 'customer' && detail && (
          <div className="halo-detail">
            <div className={`halo-status-line ${HALO_CHIP[detail.status]?.cls || ''}`}>
              {HALO_CHIP[detail.status]?.icon} {HALO_CHIP[detail.status]?.label || detail.status}
              {detail.pushed_at && <span className="halo-muted"> · pushed {formatDateTime(detail.pushed_at)}</span>}
              {detail.message && <div className="halo-muted">{detail.message}</div>}
            </div>
            {detail.exceptions?.length > 0 && (
              <div className="halo-box error">
                <strong>Blocked until fixed</strong>
                <ul>{detail.exceptions.map((e: any, i: number) => <li key={i}>{e.detail}</li>)}</ul>
                <a href={`/halopsa?tab=lines&customer=${customerId}`}>Fix mapping on the HaloPSA page →</a>
              </div>
            )}
            {detail.diff?.warnings?.length > 0 && (
              <div className="halo-box warn"><ul>{detail.diff.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}</ul></div>
            )}
            {Object.entries(detail.diff?.per_ri || {}).map(([ri, r]: [string, any]) => {
              const actions = r.actions.filter((a: any) => showUnchanged || a.op !== 'unchanged');
              const hidden = r.actions.length - actions.length;
              return (
                <div key={ri} className="halo-ri">
                  <h3>Recurring invoice {ri} <span className="halo-muted">now {ZAR(r.before_total)} → {ZAR(r.after_total)} <strong className="halo-delta">{signedZAR(String(parseFloat(r.after_total) - parseFloat(r.before_total)))}</strong></span></h3>
                  <table className="halo-diff">
                    <thead><tr><th>Line</th><th>Halo now</th><th>Snapshot</th><th>Change</th></tr></thead>
                    <tbody>
                      {actions.map((a: any, i: number) => (
                        <tr key={i} className={`op-${a.op}`}>
                          <td>{a.name} <span className="halo-muted">#{a.halo_item_id}</span></td>
                          <td>{a.op === 'add' ? '—' : `${a.qty_before} × ${ZAR(a.price_before)}`}</td>
                          <td>{a.op === 'remove' ? '—' : `${a.qty_after} × ${ZAR(a.price_after)}`}</td>
                          <td className="op">{a.op === 'unchanged' ? 'same' : a.op}</td>
                        </tr>
                      ))}
                      {actions.length === 0 && <tr><td colSpan={4} className="halo-muted">No changes on this recurring invoice.</td></tr>}
                    </tbody>
                  </table>
                  {hidden > 0 && <button className="link-btn" onClick={() => setShowUnchanged(true)}>show {hidden} unchanged line{hidden === 1 ? '' : 's'}</button>}
                </div>
              );
            })}
          </div>
        )}
        {mode === 'customer' && !detail && <p className="halo-muted">Loading the diff…</p>}

        {results && (
          <div className="halo-results">
            <strong>Results</strong>
            <ul>
              {results.map(r => {
                const c = summary.customers.find(x => x.customer_id === r.customer_id);
                return <li key={r.customer_id} className={r.status === 'pushed' ? 'ok' : 'bad'}>{HALO_CHIP[r.status]?.icon || '•'} {c?.customer_name || r.customer_id} — {HALO_CHIP[r.status]?.label || r.status}{r.message ? `: ${r.message}` : ''}</li>;
              })}
            </ul>
          </div>
        )}

        <footer>
          {canRollback && <button className="btn btn-sm btn-outline" disabled={busy} onClick={rollback}>↩ Roll back</button>}
          <span className="halo-grow" />
          <button className="btn btn-sm btn-outline" disabled={busy} onClick={onClose}>Close</button>
          {canPush && !results && (
            <button className="btn btn-sm btn-primary" disabled={busy || summary.allowlist_mode === 'off'} onClick={push}
                    title={summary.allowlist_mode === 'off' ? 'HALO_PUSH_ALLOWLIST is empty - pushing is disabled' : ''}>
              {busy ? '⏳ Pushing…' : '⬆ Confirm push'}
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}

const ZAR = (v: string | number) =>
  `R ${(typeof v === 'string' ? parseFloat(v) || 0 : v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const reconLabel = (status: string) => {
  switch (status) {
    case 'match': return '✓ match';
    case 'qty_diff': return '≠ qty differs';
    case 'm365_only': return '⚠ not billed in CloudBlue';
    case 'cloudblue_only': return 'ℹ billed, not in M365';
    default: return status;
  }
};

const SERVICE_LABELS: Record<string, string> = {
  xero: 'Xero',
  microsoft365: 'M365',
  cloudblue: 'CloudBlue',
  cove: 'Cove',
  sentinelone: 'S1',
  proxmox: 'Proxmox',
  whmcs: 'WHMCS',
  splynx: 'Splynx',
  manual: 'Manual'
};

const SERVICE_COLORS: Record<string, string> = {
  xero: '#13B5EA',
  microsoft365: '#00A4EF',
  cloudblue: '#6366F1',
  cove: '#10B981',
  sentinelone: '#8B5CF6',
  proxmox: '#F59E0B',
  whmcs: '#EF4444',
  splynx: '#0D9488',
  manual: '#64748B'
};

// Until 2026-10-22 the hidden list lived per browser in localStorage. It is a
// shared server-side flag now; whatever a browser still holds is pushed to the
// server once, then the key is dropped.
const LEGACY_HIDDEN_KEY = 'cloudview_hidden_billing_customers';

function drainLegacyHiddenIds(): number[] {
  try {
    const stored = localStorage.getItem(LEGACY_HIDDEN_KEY);
    if (!stored) return [];
    localStorage.removeItem(LEGACY_HIDDEN_KEY);
    const ids = JSON.parse(stored);
    return Array.isArray(ids) ? ids.filter((x: unknown) => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

// Flatten the details payload into excel-style rows: line items grouped per
// service with a subtotal row after each group.
interface GridRow {
  kind: 'line' | 'subtotal';
  service: string;
  firstOfGroup?: boolean;
  item?: string;
  sub?: string | null;
  sku?: string | null;
  qty?: number;
  unitPrice?: string | null;
  costPrice?: string | null;
  hasOverride?: boolean;
  priceSource?: string; // client | default | cost
  margin?: string | null;
  isPriced?: boolean;
  amount?: string;
  monthly?: string;
  cycle?: string;
  term?: string | null;
  renews?: string | null;
  xeroLinked?: boolean;
  subtotal?: string;
  itemCount?: number;
}

// Service types whose synced price is a COST price — the Sell column is
// editable and writes an org-wide selling price (price book). Manual items
// carry their own price and are edited in the full client view.
const SELL_EDITABLE = new Set(['cloudblue', 'cove', 'proxmox', 'whmcs', 'xero', 'sentinelone', 'splynx']);

function buildGridRows(details: any): GridRow[] {
  const rows: GridRow[] = [];
  const services = (details.services || [])
    .slice()
    .sort((a: any, b: any) => parseFloat(b.subtotal || '0') - parseFloat(a.subtotal || '0'));
  for (const service of services) {
    const products = service.mappings.flatMap((mapping: any) =>
      mapping.products.map((p: any) => ({ ...p, _mappingName: mapping.external_name }))
    );
    if (products.length === 0) continue;
    products.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
    products.forEach((p: any, i: number) => {
      rows.push({
        kind: 'line',
        service: service.service_type,
        firstOfGroup: i === 0,
        item: p.name,
        sub: p._mappingName && p._mappingName !== p.name ? p._mappingName : null,
        sku: p.sku,
        qty: p.quantity,
        unitPrice: p.unit_price,
        costPrice: p.cost_price,
        hasOverride: p.has_override,
        priceSource: p.price_source || 'cost',
        margin: p.margin,
        isPriced: p.is_priced,
        amount: p.amount,
        monthly: p.monthly_amount,
        cycle: p.billing_period || p.billing_cycle || 'monthly',
        term: p.subscription_period || null,
        renews: p.renewal_date || null,
        xeroLinked: p.xero_linked,
      });
    });
    rows.push({
      kind: 'subtotal',
      service: service.service_type,
      subtotal: service.subtotal,
      itemCount: products.length,
    });
  }
  return rows;
}

function Billing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [filter, setFilter] = useState<'all' | 'ready' | 'incomplete' | 'unmapped'>('all');
  const [search, setSearch] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState<number | null>(null);
  const [customerDetails, setCustomerDetails] = useState<any>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [showHidden, setShowHidden] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>(() => {
    try { return JSON.parse(localStorage.getItem('cv-billing-sort') || '') || { key: 'customer', dir: 'asc' }; }
    catch { return { key: 'customer', dir: 'asc' }; }
  });
  useEffect(() => { try { localStorage.setItem('cv-billing-sort', JSON.stringify(sort)); } catch { /* ignore */ } }, [sort]);
  const [period, setPeriod] = useState<string>(() => {
    const q = new URLSearchParams(window.location.search).get('period');
    return q && /^\d{4}-\d{2}$/.test(q) ? q : new Date().toISOString().slice(0, 7);
  });
  // ?customer=<id> (from the Integrations "Billing status" tab) opens that row once
  const [pendingCustomer, setPendingCustomer] = useState<number | null>(() => {
    const q = new URLSearchParams(window.location.search).get('customer');
    return q && /^\d+$/.test(q) ? Number(q) : null;
  });
  const [editingSell, setEditingSell] = useState<{ service: string; item: string } | null>(null);
  const [sellValue, setSellValue] = useState('');
  const [sellClientOnly, setSellClientOnly] = useState(false);
  const [savingSell, setSavingSell] = useState(false);

  // ── Billing run (month snapshot) for the selected period ──
  const [run, setRun] = useState<BillingRunInfo | null>(null);
  const [runLoading, setRunLoading] = useState(true);
  const [viewLive, setViewLive] = useState(false);
  const [starting, setStarting] = useState(false);
  const [haloSummary, setHaloSummary] = useState<HaloRunSummary | null>(null);
  const [haloDrawer, setHaloDrawer] = useState<{ mode: 'customer' | 'all'; customerId?: number } | null>(null);
  const haloByCustomer = useMemo(
    () => new Map<number, HaloRunCustomer>((haloSummary?.customers || []).map(c => [c.customer_id, c])),
    [haloSummary]);
  const runId = run?.id;
  const runOpen = run?.status === 'open';
  const runSnapshotAt = run?.snapshot_at;
  const runBusy = run?.status === 'syncing' || run?.status === 'snapshotting';
  const snapshotMode = !!run && runOpen && !viewLive;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Always the whole list: status filter, invoice filter, search, hidden and
      // flagged are applied client-side so the counts can exclude hidden customers
      // and switching tabs/sorting is instant.
      let data;
      if (runId && runOpen && !viewLive) {
        data = await billingRunsApi.getOverview(runId, undefined, undefined);
      } else {
        data = await billingApi.getOverview(undefined, undefined, period);
      }
      setOverview(data);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
    // runSnapshotAt: a re-run replaces the snapshot under the same run id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, runId, runOpen, runSnapshotAt, viewLive]);

  const handleExportCsv = () => {
    billingApi.downloadCsv();
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // One-off: push a pre-2026-10-22 per-browser hidden list to the shared flag
  useEffect(() => {
    const legacy = drainLegacyHiddenIds();
    if (legacy.length === 0) return;
    Promise.allSettled(legacy.map(id => customersApi.setHidden(id, true))).then(() => {
      toast.success(`${legacy.length} hidden customer${legacy.length === 1 ? '' : 's'} from this browser now hidden for everyone`);
      loadData();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRun = useCallback(async () => {
    setRunLoading(true);
    try {
      const res = await billingRunsApi.getForPeriod(period);
      setRun(res.run);
    } catch {
      setRun(null);
    } finally {
      setRunLoading(false);
    }
  }, [period]);

  useEffect(() => {
    setViewLive(false);
    setExpandedCustomer(null);
    loadRun();
  }, [loadRun]);

  // Halo push status for the open snapshot (compute is idempotent - pushed customers are left alone)
  const loadHalo = useCallback(async (compute = false) => {
    if (!runId || !runOpen) { setHaloSummary(null); return; }
    try {
      setHaloSummary(compute ? await haloApi.runCompute(runId) : await haloApi.runSummary(runId));
    } catch {
      setHaloSummary(null);
    }
  }, [runId, runOpen]);
  useEffect(() => { loadHalo(true); }, [loadHalo, runSnapshotAt]);
  const afterHaloChange = useCallback(async () => { await Promise.all([loadHalo(false), loadData()]); }, [loadHalo, loadData]);

  // Poll while the run is syncing / snapshotting
  useEffect(() => {
    if (!runId || !runBusy) return;
    const t = setInterval(async () => {
      try {
        const r = await billingRunsApi.get(runId);
        setRun(r);
        if (r.status === 'open') {
          setViewLive(false);
          toast.success(`${r.period_label} snapshot ready — ${r.customer_count} customers, ${ZAR(r.grand_total)} / month`, { duration: 8000 });
        } else if (r.status === 'error') {
          toast.error(r.message || 'Billing run failed', { duration: 8000 });
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [runId, runBusy]);

  const startRun = async (force = false) => {
    const label = periodLabel(period);
    const msg = force
      ? `Re-run the ${label} billing snapshot?\n\nEvery integration is synced again and the snapshot is replaced. Nothing has been ticked processed yet, so this is safe.`
      : `Bill now for ${label}?\n\nThis runs a full sync of every integration (a few minutes), then freezes every customer's billable QUANTITIES as the ${label} snapshot. You check quantities off that snapshot and tick customers off as processed; later syncs won't change the quantities. Prices stay live from the price book.`;
    if (!window.confirm(msg)) return;
    setStarting(true);
    try {
      const res = await billingRunsApi.start(period, force);
      setRun(res.run);
      setViewLive(false);
      setExpandedCustomer(null);
      toast.success(`Billing run for ${label} started — syncing every integration…`);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to start billing run');
    } finally {
      setStarting(false);
    }
  };

  const loadCustomerDetails = async (customerId: number) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      return;
    }
    try {
      const details = snapshotMode && run
        ? await billingRunsApi.getCustomerDetails(run.id, customerId)
        : await billingApi.getCustomerDetails(customerId);
      setCustomerDetails(details);
      setExpandedCustomer(customerId);
    } catch (error: any) {
      toast.error('Failed to load customer details');
    }
  };

  useEffect(() => {
    if (pendingCustomer == null || runLoading || !overview) return;
    if (overview.customers.some(c => c.customer_id === pendingCustomer)) {
      setSearch('');
      loadCustomerDetails(pendingCustomer);
      setTimeout(() => document.querySelector('.customer-row.expanded')?.scrollIntoView({ block: 'center' }), 50);
    }
    setPendingCustomer(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCustomer, runLoading, overview]);

  // ── Selling price (price book: default sell + per-client prices) ──
  // Prices are editable in snapshot mode too: the snapshot freezes QUANTITIES only,
  // every read re-prices the frozen lines from the live price book.
  const startSellEdit = (row: GridRow) => {
    if (!SELL_EDITABLE.has(row.service) || savingSell) return;
    setEditingSell({ service: row.service, item: row.item! });
    setSellValue(row.unitPrice || '');
    // editing a line that already has a client-specific price keeps that mode
    setSellClientOnly(row.priceSource === 'client');
  };

  const saveSellPrice = async () => {
    if (!editingSell || savingSell) return;
    setSavingSell(true);
    const price = sellValue.trim() === '' ? null : sellValue.trim();
    try {
      if (sellClientOnly && expandedCustomer != null) {
        const res = await billingApi.setClientPrice({
          service_type: editingSell.service,
          item_name: editingSell.item,
          customer_id: expandedCustomer,
          selling_price: price,
        });
        if (res.removed) toast.success('Client price removed — back to the default price');
        else toast.success(`Client-specific price set for this customer only`);
      } else {
        const res = await billingApi.updatePriceBookItem({
          service_type: editingSell.service,
          item_name: editingSell.item,
          selling_price: price,
        });
        if (price === null) toast.success(`Default price cleared — ${res.affected_lines} line(s) back to cost price`);
        else toast.success(`Default selling price set: ${res.affected_lines} line(s) across all customers`);
      }
      setEditingSell(null);
      if (expandedCustomer != null) {
        try {
          setCustomerDetails(snapshotMode && run
            ? await billingRunsApi.getCustomerDetails(run.id, expandedCustomer)
            : await billingApi.getCustomerDetails(expandedCustomer));
        } catch {}
      }
      loadData();
      if (snapshotMode) loadHalo(true);   // the Halo diff prices off the live price book too
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to save selling price');
    } finally {
      setSavingSell(false);
    }
  };

  // ── "Flag for Marius": shared flag + note, highlighted row, own filter ──
  const [showFlagged, setShowFlagged] = useState(false);
  const applyFlag = (customerId: number, f: { review_flag: boolean; review_note: string | null; review_flagged_by: string | null; review_flagged_at: string | null }) =>
    setOverview((o: any) => o ? { ...o, customers: o.customers.map((c: CustomerBillingItem) => c.customer_id === customerId ? { ...c, ...f } : c) } : o);
  // The flag dialog (components/FlagDialog.tsx) replaces window.prompt/confirm
  const [flagDialog, setFlagDialog] = useState<{ mode: FlagDialogMode; customer: CustomerBillingItem } | null>(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const toggleFlag = (customer: CustomerBillingItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlagDialog({ mode: customer.review_flag ? 'clear' : 'flag', customer });
  };
  const editFlagNote = (customer: CustomerBillingItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlagDialog({ mode: 'edit', customer });
  };
  const saveFlag = async (note: string) => {
    if (!flagDialog) return;
    const { customer } = flagDialog;
    setFlagBusy(true);
    try {
      applyFlag(customer.customer_id, await customersApi.setFlag(customer.customer_id, { flagged: true, note }));
      toast.success(flagDialog.mode === 'flag' ? `🚩 ${customer.customer_name} flagged` : 'Note saved');
      setFlagDialog(null);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not update the flag');
    } finally {
      setFlagBusy(false);
    }
  };
  const clearFlag = async () => {
    if (!flagDialog) return;
    const { customer } = flagDialog;
    setFlagBusy(true);
    try {
      applyFlag(customer.customer_id, await customersApi.setFlag(customer.customer_id, { flagged: false }));
      toast.success(`${customer.customer_name}: flag cleared`);
      setFlagDialog(null);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not clear the flag');
    } finally {
      setFlagBusy(false);
    }
  };

  // ── Persistent billing note: standing instructions that stay on the row month to month ──
  const [noteDialog, setNoteDialog] = useState<{ mode: FlagDialogMode; customer: CustomerBillingItem } | null>(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const applyNote = (customerId: number, n: { billing_note: string | null; billing_note_by: string | null; billing_note_at: string | null }) =>
    setOverview((o: any) => o ? { ...o, customers: o.customers.map((c: CustomerBillingItem) => c.customer_id === customerId ? { ...c, ...n } : c) } : o);
  const openNote = (customer: CustomerBillingItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setNoteDialog({ mode: 'edit', customer });
  };
  const saveNote = async (text: string) => {
    if (!noteDialog) return;
    const { customer } = noteDialog;
    if (!text.trim() && !customer.billing_note) { setNoteDialog(null); return; }
    setNoteBusy(true);
    try {
      const r = await customersApi.setNote(customer.customer_id, text.trim() || null);
      applyNote(customer.customer_id, r);
      toast.success(r.billing_note ? `📝 ${customer.customer_name}: note saved` : `${customer.customer_name}: note removed`);
      setNoteDialog(null);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not save the note');
    } finally {
      setNoteBusy(false);
    }
  };
  const removeNote = async () => {
    if (!noteDialog) return;
    if (noteDialog.mode !== 'clear') { setNoteDialog({ ...noteDialog, mode: 'clear' }); return; }
    const { customer } = noteDialog;
    setNoteBusy(true);
    try {
      applyNote(customer.customer_id, await customersApi.setNote(customer.customer_id, null));
      toast.success(`${customer.customer_name}: note removed`);
      setNoteDialog(null);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not remove the note');
    } finally {
      setNoteBusy(false);
    }
  };

  // "Processed" tick: the ONE per-customer month checklist, shared by the team.
  // Live view and snapshot alike; on an open snapshot the server mirrors it onto
  // the run row (invoiced / pending), which is what the Halo push and the
  // Integrations "Billing status" tab read.
  const [processedFilter, setProcessedFilter] = useState<'all' | 'done' | 'todo'>('all');
  const [processing, setProcessing] = useState<number | null>(null);
  const toggleProcessed = async (customer: CustomerBillingItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (processing) return;
    setProcessing(customer.customer_id);
    try {
      const r = await billingApi.setProcessed(customer.customer_id, { period, checked: !customer.processed });
      setOverview((o: any) => o ? { ...o, customers: o.customers.map((c: CustomerBillingItem) => c.customer_id === customer.customer_id
        ? { ...c, processed: r.processed, processed_by: r.processed_by, processed_at: r.processed_at, processed_note: r.processed_note,
            invoice_status: c.invoice_status ? (r.processed ? 'invoiced' : 'pending') : c.invoice_status } : c) } : o);
      if (runId && runOpen) billingRunsApi.get(runId).then(setRun).catch(() => { /* banner counts refresh on next load */ });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not update the check');
    } finally {
      setProcessing(null);
    }
  };

  const setHiddenLocally = (ids: number[], hidden: boolean) =>
    setOverview((o: any) => o ? { ...o, customers: o.customers.map((c: CustomerBillingItem) =>
      ids.includes(c.customer_id) ? { ...c, hidden } : c) } : o);

  const toggleHideCustomer = async (customerId: number, customerName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const hidden = !hiddenCustomers.has(customerId);
    try {
      await customersApi.setHidden(customerId, hidden);
      setHiddenLocally([customerId], hidden);
      toast.success(hidden ? `${customerName} hidden from billing (for everyone)` : `${customerName} is now visible`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not update the hidden state');
    }
  };

  const unhideAll = async () => {
    const ids = Array.from(hiddenCustomers);
    const results = await Promise.allSettled(ids.map(id => customersApi.setHidden(id, false)));
    const done = ids.filter((_, i) => results[i].status === 'fulfilled');
    setHiddenLocally(done, false);
    setShowHidden(false);
    toast.success(done.length === ids.length ? 'All customers restored' : `${done.length} of ${ids.length} customers restored`);
  };

  // Cloud ITS / WebCafe split: per-customer entity, shared by the org, highlights the row
  const setEntity = async (customer: CustomerBillingItem, entity: 'cloudits' | 'webcafe' | null) => {
    try {
      const r = await customersApi.setEntity(customer.customer_id, entity);
      setOverview(prev => prev ? { ...prev, customers: prev.customers.map(c => c.customer_id === customer.customer_id ? { ...c, billing_entity: r.billing_entity as any } : c) } : prev);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Could not save the billing entity');
    }
  };

  // "Sync from integrations": only re-reads what the integration pages already hold (seconds, no external calls)
  const [quickSyncing, setQuickSyncing] = useState(false);
  const handleQuickSync = async () => {
    setQuickSyncing(true);
    try {
      const r = await billingApi.refreshLines();
      const parts = Object.entries(r.integrations || {})
        .filter(([k]) => k !== 'price_book')
        .map(([k, v]: [string, any]) => `${k} ${v.lines ?? v.mappings ?? 0}`);
      const errs = Object.keys(r.errors || {});
      (errs.length ? toast.error : toast.success)(
        `Invoice Prep refreshed from the integration pages in ${r.seconds}s · lines: ${parts.join(' · ')}${errs.length ? ` · failed: ${errs.join(', ')}` : ''}`,
        { duration: 8000 });
      await loadData();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Sync from integrations failed');
    } finally {
      setQuickSyncing(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing('all');
    try {
      await integrationsApi.syncAll();
      toast.success('Full sync started — progress on the Integrations page. Reload here in a few minutes.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
    } finally {
      setSyncing(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getBillingStatusClass = (status: string) => {
    switch (status) {
      case 'ready': return 'status-ready';
      case 'incomplete': return 'status-incomplete';
      case 'unmapped': return 'status-unmapped';
      default: return '';
    }
  };

  // ── Visible list: hidden customers are out of every count and total ──
  const allCustomers = overview?.customers || [];
  const hiddenCustomers = new Set(allCustomers.filter(c => c.hidden).map(c => c.customer_id));
  const hiddenCount = hiddenCustomers.size;
  const shownCustomers = allCustomers.filter(c => !hiddenCustomers.has(c.customer_id));
  const flaggedCount = shownCustomers.filter(c => c.review_flag).length;
  const num = (v: string | number | null | undefined) => (typeof v === 'number' ? v : parseFloat(v || '0') || 0);
  const stats = {
    total: shownCustomers.length,
    ready: shownCustomers.filter(c => c.billing_status === 'ready').length,
    incomplete: shownCustomers.filter(c => c.billing_status === 'incomplete').length,
    unmapped: shownCustomers.filter(c => c.billing_status === 'unmapped').length,
    grand_total: shownCustomers.reduce((t, c) => t + num(c.monthly_total), 0),
    ready_total: shownCustomers.filter(c => c.billing_status === 'ready').reduce((t, c) => t + num(c.monthly_total), 0),
    needing_pricing: shownCustomers.filter(c => c.billing_status === 'ready' && c.unpriced_product_count > 0).length,
    m365_mismatches: shownCustomers.filter(c => c.m365_check === 'mismatch').length,
    m365_licenses: shownCustomers.reduce((t, c) => t + (c.m365_total || 0), 0),
    cove_devices: shownCustomers.reduce((t, c) => t + (c.services || []).filter(sv => sv.service_type === 'cove').reduce((x, sv) => x + (sv.product_count || 0), 0), 0),
    processed: shownCustomers.filter(c => c.processed).length,
    processed_total: shownCustomers.filter(c => c.processed).reduce((t, c) => t + num(c.monthly_total), 0),
    to_process_total: shownCustomers.filter(c => !c.processed).reduce((t, c) => t + num(c.monthly_total), 0),
  };
  const q = search.trim().toLowerCase();
  const matchesSearch = (c: CustomerBillingItem) => !q || c.customer_name.toLowerCase().includes(q) || (c.contact_email || '').toLowerCase().includes(q);
  const baseList = showFlagged
    ? shownCustomers.filter(c => c.review_flag)
    : showHidden
    ? allCustomers.filter(c => hiddenCustomers.has(c.customer_id))
    : shownCustomers;
  const filteredCustomers = baseList.filter(c =>
    matchesSearch(c)
    && (showHidden || showFlagged || filter === 'all' || c.billing_status === filter)
    && (showHidden || showFlagged || processedFilter === 'all'
        || (processedFilter === 'done' ? !!c.processed : !c.processed)));

  // ── Sorting: click a column header, click again to flip ──
  const STATUS_ORDER: Record<string, number> = { ready: 0, incomplete: 1, unmapped: 2 };
  const serviceCount = (c: CustomerBillingItem) =>
    [c.has_microsoft365, c.has_cove, c.has_xero, c.has_cloudblue, c.has_sentinelone, c.has_whmcs, c.has_splynx, c.has_portaone, c.has_manual].filter(Boolean).length;
  const sortValue = (c: CustomerBillingItem, key: SortKey): string | number => {
    switch (key) {
      case 'status': return STATUS_ORDER[c.billing_status] ?? 9;
      case 'customer': return c.customer_name.toLowerCase();
      case 'services': return serviceCount(c);
      case 'm365': return c.m365_check === 'mismatch' ? 0 : c.m365_check === 'match' ? 1 : 2;
      case 'products': return c.total_products;
      case 'quantity': return c.total_quantity;
      case 'amount': return num(c.monthly_total);
      case 'processed': return c.processed ? 1 : 0;
      case 'actions': return c.review_flag ? 0 : 1;
      default: return 0;
    }
  };
  const NUMERIC_DEFAULT_DESC: SortKey[] = ['services', 'products', 'quantity', 'amount'];
  const clickSort = (key: SortKey) => setSort(prev => prev.key === key
    ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: NUMERIC_DEFAULT_DESC.includes(key) ? 'desc' : 'asc' });
  const sortArrow = (key: SortKey) => sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const visibleCustomers = [...filteredCustomers].sort((a, b) => {
    const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
    let r = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
    if (r === 0) r = a.customer_name.localeCompare(b.customer_name);
    return sort.dir === 'asc' ? r : -r;
  });

  if (loading && !overview) {
    return <div className="billing-page"><div className="loading">Loading billing data...</div></div>;
  }

  return (
    <div className="billing-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>💳 Invoice Prep</h1>
          <p className="page-subtitle">Customers, services & billing — the single source of truth for the 25th-of-month run</p>
        </div>
        <div className="header-actions">
          <input
            type="month"
            className="period-input"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            title="Billing period"
          />
          {run && runOpen ? (
            <>
              <button
                className={`btn btn-sm ${viewLive ? 'btn-outline' : 'btn-snapshot'}`}
                onClick={() => { setViewLive(v => !v); setExpandedCustomer(null); }}
                title={viewLive ? 'Back to the frozen month snapshot you bill from' : 'Show the live, still-changing data instead of the snapshot'}
              >
                {viewLive ? '📸 Show snapshot' : '📡 Show live data'}
              </button>
              {!run.locked && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => startRun(true)}
                  disabled={starting}
                  title="Sync every integration again and replace the snapshot (only possible while nothing is ticked processed)"
                >
                  ↻ Re-run snapshot
                </button>
              )}
            </>
          ) : runBusy ? (
            <button className="btn btn-primary btn-sm" disabled>⏳ Building snapshot…</button>
          ) : (
            <button
              className="btn btn-primary btn-sm btn-billnow"
              onClick={() => startRun(false)}
              disabled={starting || runLoading}
              title="Full sync of every integration, then freeze this month's billable lines as the snapshot to invoice from"
            >
              📸 {run?.status === 'error' ? 'Retry billing run for' : 'Bill now for'} {periodLabel(period)}
            </button>
          )}
          <button className="btn btn-outline btn-sm" onClick={handleExportCsv} title="Line items from the LIVE data">
            ⬇ Export CSV
          </button>
          <button
            className="btn btn-outline btn-sm btn-quicksync"
            onClick={handleQuickSync}
            disabled={quickSyncing}
            title="Quick: rebuild the billing lines from what the integration pages already show (CloudBlue, Splynx, SentinelOne, Redstor, PortaOne caches). No calls to the sources - seconds."
          >
            {quickSyncing ? '⏳ Refreshing…' : '⚡ Sync from integrations'}
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={handleSyncAll}
            disabled={syncing !== null}
            title="Full: runs every configured integration against its source through the sync framework (same as nightly, a few minutes)"
          >
            {syncing === 'all' ? '⏳ Starting…' : '🔄 Sync all data'}
          </button>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => navigate('/integrations')}
            title="Per-integration refresh, sync history and nightly settings"
          >
            🔌 Integrations
          </button>
        </div>
      </div>

      {/* Billing run banner */}
      {run && (
        <div className={`run-banner ${run.status} ${runOpen && run.sync_failed_billable.length ? 'warn' : ''} ${runOpen && viewLive ? 'live' : ''}`}>
          <div className="run-banner-main">
            {run.status === 'syncing' && (
              <>
                <span className="run-title">⏳ Building the {run.period_label} snapshot</span>
                <span className="run-sub">Syncing every integration (started {formatDate(run.sync_started_at)}) — refreshes every 5 s</span>
              </>
            )}
            {run.status === 'snapshotting' && (
              <span className="run-title">📸 Freezing billable lines for {run.period_label}…</span>
            )}
            {run.status === 'error' && (
              <>
                <span className="run-title">❌ {run.period_label} billing run failed</span>
                <span className="run-sub">{run.message}</span>
              </>
            )}
            {runOpen && (
              <>
                <span className="run-title">
                  📸 {run.period_label} snapshot
                  {viewLive && <span className="run-live-tag">viewing LIVE data — the snapshot is what you bill from</span>}
                </span>
                <span className="run-sub">
                  Taken {formatDate(run.snapshot_at)}{run.created_by ? ` by ${run.created_by}` : ''} ·{' '}
                  {run.customer_count} customers · {run.line_count} lines · {ZAR(run.grand_total)} / month
                  {run.complete
                    ? ' · ✅ everything processed'
                    : ` · ${run.invoiced_count}/${run.customer_count} processed, ${ZAR(run.outstanding_total)} still to process`}
                </span>
                {run.sync_failed_billable.length > 0 && (
                  <span className="run-warn">
                    ⚠️ Billable integration(s) failed to sync before the snapshot: {run.sync_failed_billable.join(', ')} — those lines may be stale.
                    {!run.locked && ' Fix the integration and use "Re-run snapshot".'}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="run-banner-side">
            {Object.keys(run.sync_summary || {}).length > 0 && (
              <div className="run-chips" title="Per-integration sync outcome for this run">
                {Object.entries(run.sync_summary).map(([name, s]) => (
                  <span key={name} className={`run-chip ${s.status}`} title={s.message || s.status}>
                    {SYNC_ICON[s.status] || '•'} {name}{typeof s.items === 'number' ? ` · ${s.items}` : ''}
                  </span>
                ))}
                {run.status === 'syncing' && <span className="run-chip running">⏳ …</span>}
              </div>
            )}
            {runOpen && (
              <button className="btn btn-sm btn-outline" onClick={() => navigate('/integrations?tab=billing')}
                      title="Billed / outstanding per integration for this snapshot">
                🔌 Billed per integration →
              </button>
            )}
            {runOpen && haloSummary && (
              <div className="halo-banner">
                {haloSummary.fire_at && (
                  <span className="halo-countdown" title="When HaloPSA generates the recurring invoices (earliest confirmed RI)">
                    🔥 Halo fires {formatDateTime(haloSummary.fire_at)}{haloSummary.hours_left != null ? ` · ${haloSummary.hours_left} h left` : ''}
                  </span>
                )}
                <button className="btn btn-sm btn-primary"
                        disabled={!(haloSummary.counts.ready > 0) || haloSummary.allowlist_mode === 'off'}
                        onClick={() => setHaloDrawer({ mode: 'all' })}
                        title={haloSummary.allowlist_mode === 'off' ? 'Pushing is disabled: HALO_PUSH_ALLOWLIST is empty' : 'Review and push every ready customer to HaloPSA'}>
                  ⬆ Push to Halo ({haloSummary.counts.ready || 0} ready)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {flagDialog && (
        <FlagDialog mode={flagDialog.mode} customerName={flagDialog.customer.customer_name}
                    note={flagDialog.customer.review_note} flaggedBy={flagDialog.customer.review_flagged_by}
                    flaggedAt={flagDialog.customer.review_flagged_at} busy={flagBusy}
                    onConfirm={saveFlag} onClear={clearFlag} onCancel={() => { if (!flagBusy) setFlagDialog(null); }} />
      )}

      {noteDialog && (
        <FlagDialog variant="note" mode={noteDialog.mode} customerName={noteDialog.customer.customer_name}
                    note={noteDialog.customer.billing_note} flaggedBy={noteDialog.customer.billing_note_by}
                    flaggedAt={noteDialog.customer.billing_note_at} busy={noteBusy}
                    onConfirm={saveNote} onClear={noteDialog.customer.billing_note ? removeNote : undefined}
                    onCancel={() => { if (!noteBusy) setNoteDialog(null); }} />
      )}

      {haloDrawer && runId && haloSummary && (
        <HaloPushDrawer runId={runId} mode={haloDrawer.mode} customerId={haloDrawer.customerId} summary={haloSummary}
                        onClose={() => setHaloDrawer(null)} onChanged={afterHaloChange} />
      )}

      {/* Summary Cards */}
      {overview && (
        <div className="summary-grid">
          <div
            className={`summary-card processed ${processedFilter === 'done' ? 'active' : ''}`}
            onClick={() => setProcessedFilter(f => f === 'done' ? 'all' : 'done')}
            title={snapshotMode ? 'Customers ticked off as processed on this snapshot' : 'Customers ticked off as processed this month'}
          >
            <div className="summary-icon">☑</div>
            <div className="summary-content">
              <div className="summary-value">{stats.processed} / {stats.total}</div>
              <div className="summary-label">Processed · {ZAR(stats.processed_total)}</div>
            </div>
          </div>
          {snapshotMode && (
            <div
              className={`summary-card to-invoice ${processedFilter === 'todo' ? 'active' : ''}`}
              onClick={() => setProcessedFilter(f => f === 'todo' ? 'all' : 'todo')}
              title="Customers in the snapshot not yet ticked processed"
            >
              <div className="summary-icon">🧾</div>
              <div className="summary-content">
                <div className="summary-value">{ZAR(stats.to_process_total)}</div>
                <div className="summary-label">Still to process ({stats.total - stats.processed})</div>
              </div>
            </div>
          )}
          <div className="summary-card money">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <div className="summary-value">{ZAR(stats.grand_total)}</div>
              <div className="summary-label">{snapshotMode ? 'Snapshot total / month' : 'Billable / month'}</div>
            </div>
          </div>
          <div className="summary-card money-ready">
            <div className="summary-icon">🧾</div>
            <div className="summary-content">
              <div className="summary-value">{ZAR(stats.ready_total)}</div>
              <div className="summary-label">Ready to Invoice</div>
            </div>
          </div>
          <div
            className="summary-card needs-pricing"
            title="Ready customers that still have unpriced products"
          >
            <div className="summary-icon">🏷️</div>
            <div className="summary-content">
              <div className="summary-value">{stats.needing_pricing}</div>
              <div className="summary-label">Need Pricing</div>
            </div>
          </div>
          <div
            className="summary-card m365-mismatch"
            title="Customers where Microsoft 365 license counts do not match CloudBlue"
          >
            <div className="summary-icon">🔁</div>
            <div className="summary-content">
              <div className="summary-value">{stats.m365_mismatches}</div>
              <div className="summary-label">M365 ≠ CloudBlue</div>
            </div>
          </div>
          {/* Total / Ready / Incomplete / Unmapped tiles removed 2026-10-23: the
              filter tabs right below carry the same four counts. */}
          <div className="summary-card m365">
            <div className="summary-icon">🔷</div>
            <div className="summary-content">
              <div className="summary-value">{stats.m365_licenses}</div>
              <div className="summary-label">M365 Licenses</div>
            </div>
          </div>
          <div className="summary-card cove">
            <div className="summary-icon">💾</div>
            <div className="summary-content">
              <div className="summary-value">{stats.cove_devices}</div>
              <div className="summary-label">Cove Devices</div>
            </div>
          </div>
        </div>
      )}

      {/* Last Sync Info */}
      {overview && !snapshotMode && (
        <div className="sync-status-bar">
          <span className="sync-item">
            <span className="sync-label">Xero:</span>
            <span className="sync-time">{formatDate(overview.last_xero_sync)}</span>
          </span>
          <span className="sync-item">
            <span className="sync-label">M365:</span>
            <span className="sync-time">{formatDate(overview.last_m365_sync)}</span>
          </span>
          <span className="sync-item">
            <span className="sync-label">Cove:</span>
            <span className="sync-time">{formatDate(overview.last_cove_sync)}</span>
          </span>
        </div>
      )}

      {/* Search, Filters, and Hidden toggle */}
      <div className="filters-bar">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' && !showHidden ? 'active' : ''}`}
            onClick={() => { setFilter('all'); setShowHidden(false); }}
          >
            All ({stats.total})
          </button>
          <button
            className={`filter-tab ready ${filter === 'ready' ? 'active' : ''}`}
            onClick={() => { setFilter('ready'); setShowHidden(false); }}
          >
            ✅ Ready ({stats.ready})
          </button>
          <button
            className={`filter-tab incomplete ${filter === 'incomplete' ? 'active' : ''}`}
            onClick={() => { setFilter('incomplete'); setShowHidden(false); }}
          >
            ⚠️ Incomplete ({stats.incomplete})
          </button>
          <button
            className={`filter-tab unmapped ${filter === 'unmapped' ? 'active' : ''}`}
            onClick={() => { setFilter('unmapped'); setShowHidden(false); }}
          >
            ❌ Unmapped ({stats.unmapped})
          </button>
          <button
            className={`filter-tab processed ${processedFilter === 'todo' ? 'active' : ''}`}
            onClick={() => { setProcessedFilter(f => f === 'todo' ? 'all' : 'todo'); setShowHidden(false); }}
            title="Customers not yet ticked as processed for this month"
          >
            ☐ To process ({stats.total - stats.processed})
          </button>
          <button
            className={`filter-tab processed ${processedFilter === 'done' ? 'active' : ''}`}
            onClick={() => { setProcessedFilter(f => f === 'done' ? 'all' : 'done'); setShowHidden(false); }}
            title="Customers ticked as processed for this month"
          >
            ☑ Processed ({stats.processed})
          </button>
          {(flaggedCount > 0 || showFlagged) && (
            <button
              className={`filter-tab flagged ${showFlagged ? 'active' : ''}`}
              onClick={() => { setShowFlagged(!showFlagged); setShowHidden(false); }}
              title="Customers the team flagged for Marius (shared with everyone)"
            >
              🚩 Flagged ({flaggedCount})
            </button>
          )}
          {hiddenCount > 0 && (
            <button
              className={`filter-tab hidden ${showHidden ? 'active' : ''}`}
              onClick={() => { setShowHidden(!showHidden); setShowFlagged(false); }}
            >
              👁️‍🗨️ Hidden ({hiddenCount})
            </button>
          )}
        </div>
        <input
          type="text"
          className="search-input"
          placeholder="Search customers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Hidden customers banner */}
      {showHidden && (
        <div className="hidden-banner">
          <span>Showing {hiddenCount} hidden customer{hiddenCount !== 1 ? 's' : ''}. Click the eye icon to restore.</span>
          <button className="btn btn-sm btn-outline" onClick={unhideAll}>
            Unhide All
          </button>
        </div>
      )}

      {/* Customer Table */}
      <div className="billing-table-container">
        <table className="billing-table">
          <thead>
            <tr>
              <th className={`col-status sortable ${sort.key === 'status' ? 'sorted' : ''}`} onClick={() => clickSort('status')} title="Sort: ready → incomplete → unmapped">Status{sortArrow('status')}</th>
              <th className={`col-customer sortable ${sort.key === 'customer' ? 'sorted' : ''}`} onClick={() => clickSort('customer')} title="Sort A → Z">Customer{sortArrow('customer')}</th>
              <th className={`col-services sortable ${sort.key === 'services' ? 'sorted' : ''}`} onClick={() => clickSort('services')} title="Sort by number of services">Services{sortArrow('services')}</th>
              <th className={`col-m365check sortable ${sort.key === 'm365' ? 'sorted' : ''}`} onClick={() => clickSort('m365')} title="Sort: mismatches first">M365 ↔ CB{sortArrow('m365')}</th>
              <th className={`col-products sortable ${sort.key === 'products' ? 'sorted' : ''}`} onClick={() => clickSort('products')} title="Billable products (Microsoft 365 check lines are not counted) — click to sort">Products{sortArrow('products')}</th>
              <th className={`col-quantity sortable ${sort.key === 'quantity' ? 'sorted' : ''}`} onClick={() => clickSort('quantity')} title="Billable quantity (Microsoft 365 check lines are not counted) — click to sort">Qty{sortArrow('quantity')}</th>
              <th className={`col-amount sortable ${sort.key === 'amount' ? 'sorted' : ''}`} onClick={() => clickSort('amount')} title="Sort by monthly amount">Amount /mo{sortArrow('amount')}</th>
              <th className={`col-processed sortable ${sort.key === 'processed' ? 'sorted' : ''}`} onClick={() => clickSort('processed')} title="The month checklist: tick when this customer's quantities are checked and the invoice is processed. Shared by the team; on a snapshot it also counts as invoiced.">Processed{sortArrow('processed')}</th>
              {snapshotMode && haloSummary && <th className="col-halo" title="HaloPSA push status for this snapshot">Halo</th>}
              <th className={`col-actions sortable ${sort.key === 'actions' ? 'sorted' : ''}`} onClick={() => clickSort('actions')} title="Sort: flagged customers first">Actions{sortArrow('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.map(customer => (
              <React.Fragment key={customer.customer_id}>
                <tr
                  className={`customer-row ${getBillingStatusClass(customer.billing_status)} ${expandedCustomer === customer.customer_id ? 'expanded' : ''} ${showHidden ? 'hidden-row' : ''} ${customer.review_flag ? 'flagged' : ''} ${customer.billing_entity ? `entity-${customer.billing_entity}` : ''}`}
                  onClick={() => loadCustomerDetails(customer.customer_id)}
                >
                  <td className="col-status">
                    <span className={`billing-badge ${customer.billing_status}`}>
                      {customer.billing_status === 'ready' && '✅'}
                      {customer.billing_status === 'incomplete' && '⚠️'}
                      {customer.billing_status === 'unmapped' && '❌'}
                    </span>
                  </td>
                  <td className="col-customer">
                    <div className="customer-name">
                      {customer.customer_name}
                      <select
                        className={`entity-select ${customer.billing_entity || 'none'}`}
                        value={customer.billing_entity || ''}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); setEntity(customer, (e.target.value || null) as any); }}
                        title="Which of our companies invoices this customer (shared with the whole team)"
                      >
                        <option value="">— entity —</option>
                        <option value="cloudits">Cloud ITS</option>
                        <option value="webcafe">WebCafe</option>
                      </select>
                    </div>
                    {customer.review_flag && (
                      <div className="flag-chip" onClick={(e) => editFlagNote(customer, e)}
                           title={`Flagged${customer.review_flagged_by ? ` by ${customer.review_flagged_by}` : ''}${customer.review_flagged_at ? ` on ${new Date(customer.review_flagged_at).toLocaleString()}` : ''} — click to edit the note`}>
                        🚩 {customer.review_note || 'Needs a look'}
                        <span className="flag-by">{customer.review_flagged_by ? ` · ${customer.review_flagged_by}` : ''}</span>
                      </div>
                    )}
                    {customer.billing_note && (
                      <div className="note-chip" onClick={(e) => openNote(customer, e)}
                           title={`Billing note${customer.billing_note_by ? ` · last edited by ${customer.billing_note_by}` : ''}${customer.billing_note_at ? ` on ${new Date(customer.billing_note_at).toLocaleString()}` : ''} — click to edit`}>
                        📝 {customer.billing_note}
                      </div>
                    )}
                    {customer.contact_email && (
                      <div className="customer-email">{customer.contact_email}</div>
                    )}
                  </td>
                  <td className="col-services">
                    <div className="service-pills">
                      {customer.has_microsoft365 && (
                        <span className="service-pill m365" title="Microsoft 365">M365</span>
                      )}
                      {customer.has_cove && (
                        <span className="service-pill cove" title="Cove Backup">Cove</span>
                      )}
                      {customer.has_xero && (
                        <span className="service-pill xero" title="Xero">Xero</span>
                      )}
                      {customer.has_cloudblue && (
                        <span className="service-pill cloudblue" title="CloudBlue">CB</span>
                      )}
                      {customer.has_sentinelone && (
                        <span className="service-pill sentinelone" title="SentinelOne">S1</span>
                      )}
                      {customer.has_whmcs && (
                        <span className="service-pill whmcs" title="WHMCS">WHMCS</span>
                      )}
                      {customer.has_splynx && (
                        <span className="service-pill splynx" title="Splynx">Splynx</span>
                      )}
                      {customer.has_portaone && (
                        <span className="service-pill portaone" title="PortaOne voice usage">Voice</span>
                      )}
                      {customer.has_redstor && (
                        <span className="service-pill redstor" title="Redstor cloud backup">Redstor</span>
                      )}
                      {customer.has_manual && (
                        <span className="service-pill manual" title="Manual services">Manual</span>
                      )}
                      {!customer.has_microsoft365 && !customer.has_cove && !customer.has_xero &&
                       !customer.has_cloudblue && !customer.has_sentinelone && !customer.has_whmcs &&
                       !customer.has_splynx && !customer.has_manual && (
                        <span className="service-pill none">None</span>
                      )}
                    </div>
                    {(customer.unpriced_product_count > 0 || customer.xero_unlinked_count > 0) && (
                      <div className="readiness-chips">
                        {customer.unpriced_product_count > 0 && (
                          <span className="chip warn" title="Products with no price">{customer.unpriced_product_count} unpriced</span>
                        )}
                        {customer.xero_unlinked_count > 0 && (
                          <span className="chip info" title="Products not linked to a Xero item">{customer.xero_unlinked_count} not in Xero</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="col-m365check">
                    {customer.m365_check === 'match' && (
                      <span className="m365-badge ok" title={`Microsoft 365 (${customer.m365_total}) matches CloudBlue (${customer.cloudblue_m365_total})`}>✓ M365 = CB</span>
                    )}
                    {customer.m365_check === 'mismatch' && (
                      <span className="m365-badge bad" title={`Microsoft 365 (${customer.m365_total}) vs CloudBlue (${customer.cloudblue_m365_total}) — ${customer.m365_mismatch_lines} line(s) differ`}>✗ M365 ≠ CB</span>
                    )}
                    {customer.m365_check === 'no_m365' && (
                      <span className="m365-badge na" title="No Microsoft 365 data to check against">—</span>
                    )}
                  </td>
                  <td className="col-products">{customer.total_products}</td>
                  <td className="col-quantity">{customer.total_quantity}</td>
                  <td className="col-amount">{parseFloat(customer.monthly_total) > 0 ? <strong>{ZAR(customer.monthly_total)}</strong> : <span className="amount-zero">—</span>}</td>
                  <td className="col-processed" onClick={e => e.stopPropagation()}>
                    <button
                      className={`proc-check ${customer.processed ? 'on' : ''}`}
                      disabled={processing === customer.customer_id}
                      onClick={e => toggleProcessed(customer, e)}
                      title={customer.processed
                        ? `Processed${customer.processed_by ? ` by ${customer.processed_by}` : ''}${customer.processed_at ? ` · ${new Date(customer.processed_at).toLocaleString()}` : ''}${customer.processed_note ? ` · ${customer.processed_note}` : ''}${customer.invoice_ref ? ` · ${customer.invoice_ref}` : ''} — click to untick`
                        : `Tick when this customer's ${period} quantities are checked and the invoice is processed`}
                    >
                      {processing === customer.customer_id ? '…' : customer.processed ? '☑' : '☐'}
                      <span className="proc-label">{customer.processed ? 'Processed' : 'Not yet'}</span>
                    </button>
                    {customer.processed && customer.processed_by && <div className="proc-by">{customer.processed_by}</div>}
                  </td>
                  {snapshotMode && haloSummary && (
                    <td className="col-halo" onClick={e => e.stopPropagation()}>
                      <HaloChip row={haloByCustomer.get(customer.customer_id)}
                                onOpen={() => setHaloDrawer({ mode: 'customer', customerId: customer.customer_id })}
                                onMapping={() => navigate('/halopsa?tab=clients')} />
                    </td>
                  )}
                  <td className="col-actions">
                    <div className="action-buttons">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customers/${customer.customer_id}/services`);
                        }}
                      >
                        View
                      </button>
                      <button
                        className={`btn btn-sm btn-icon flag-btn ${customer.review_flag ? 'on' : ''}`}
                        title={customer.review_flag ? 'Flagged for Marius — click to clear' : 'Flag this customer for Marius to look at (visible to everyone)'}
                        onClick={(e) => toggleFlag(customer, e)}
                      >
                        {customer.review_flag ? '🚩' : '⚑'}
                      </button>
                      <button
                        className={`btn btn-sm btn-icon note-btn ${customer.billing_note ? 'on' : ''}`}
                        title={customer.billing_note ? 'Billing note — click to edit (stays month to month)' : 'Add a billing note: standing instructions for this customer, kept month to month'}
                        onClick={(e) => openNote(customer, e)}
                      >
                        📝
                      </button>
                      <button
                        className="btn btn-sm btn-icon"
                        title={hiddenCustomers.has(customer.customer_id) ? 'Unhide customer' : 'Hide customer (no active billing)'}
                        onClick={(e) => toggleHideCustomer(customer.customer_id, customer.customer_name, e)}
                      >
                        {hiddenCustomers.has(customer.customer_id) ? '👁️' : '🙈'}
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedCustomer === customer.customer_id && customerDetails && (
                  <tr className="detail-row">
                    <td colSpan={9}>
                      <div className="customer-detail-panel quick-view">
                        <div className="quick-view-head">
                          <h4>{snapshotMode && run ? `📸 ${run.period_label} snapshot — frozen quantities, live prices` : 'Quick billing view'}</h4>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/customers/${customer.customer_id}/services`)}
                            title="Aliases, manual services, licensed users, mappings"
                          >
                            Full client view →
                          </button>
                        </div>

                        {/* Billable line items — excel-style grid */}
                        {(() => {
                          const rows = buildGridRows(customerDetails);
                          if (rows.length === 0) {
                            return <p className="no-services">No billable products. Open the full client view to add services, or run a sync.</p>;
                          }
                          return (
                            <>
                            <table className="xl-grid">
                              <thead>
                                <tr>
                                  <th className="svc">Service</th>
                                  <th>Item</th>
                                  <th>SKU</th>
                                  <th className="num">Qty</th>
                                  <th className="num" title="Price synced from the integration (CloudBlue = reseller cost)">Cost</th>
                                  <th className="num" title="Our selling price — click a cell to set it org-wide for that item">Sell</th>
                                  <th className="num">Amount</th>
                                  <th className="num">R /mo</th>
                                  <th title="Billing period · subscription term (CloudBlue)">Billing / term</th>
                                  <th className="ctr">Xero</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((row, i) => row.kind === 'line' ? (
                                  <tr key={i} className="xl-line">
                                    <td className="svc">
                                      {row.firstOfGroup && (
                                        <span className="xl-svc-name">
                                          <span className="xl-dot" style={{ background: SERVICE_COLORS[row.service] || '#999' }} />
                                          {SERVICE_LABELS[row.service] || row.service}
                                        </span>
                                      )}
                                    </td>
                                    <td className="item" title={row.sub ? `${row.item} · ${row.sub}` : row.item}>
                                      {row.item}
                                      {row.sub && <span className="xl-sub"> · {row.sub}</span>}
                                    </td>
                                    <td className="sku">{row.sku || ''}</td>
                                    <td className="num qty">{row.qty}</td>
                                    <td className="num cost">
                                      {row.costPrice && parseFloat(row.costPrice) > 0 ? ZAR(row.costPrice) : <span className="xl-unpriced">—</span>}
                                    </td>
                                    {editingSell && editingSell.service === row.service && editingSell.item === row.item ? (
                                      <td className="num sell editing">
                                        <div className="xl-sell-edit">
                                          <input
                                            className="xl-sell-input"
                                            autoFocus
                                            value={sellValue}
                                            disabled={savingSell}
                                            placeholder="cost"
                                            onChange={e => setSellValue(e.target.value)}
                                            onKeyDown={e => {
                                              if (e.key === 'Enter') saveSellPrice();
                                              if (e.key === 'Escape') setEditingSell(null);
                                            }}
                                            onBlur={() => { if (!savingSell) setEditingSell(null); }}
                                          />
                                          <label
                                            className={`xl-sell-scope ${sellClientOnly ? 'on' : ''}`}
                                            title="Ticked: price applies to this client only (overrides the default). Unticked: sets the default price for all clients."
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={() => setSellClientOnly(v => !v)}
                                          >
                                            👤 this client only
                                          </label>
                                        </div>
                                      </td>
                                    ) : (
                                      <td
                                        className={`num sell ${SELL_EDITABLE.has(row.service) ? 'editable' : ''} ${row.hasOverride ? 'override' : ''}`}
                                        onClick={() => startSellEdit(row)}
                                        title={
                                          !SELL_EDITABLE.has(row.service)
                                            ? (row.service === 'manual' ? 'Manual items are priced directly — edit in the full client view' : '')
                                            : row.priceSource === 'client'
                                              ? `Client-specific price${row.margin ? ` · margin ${ZAR(row.margin)}/unit` : ''} — click to change, clear to fall back to the default`
                                              : row.priceSource === 'default'
                                                ? `Default selling price${row.margin ? ` · margin ${ZAR(row.margin)}/unit` : ''} — click to change (or tick "this client only" for a client price)`
                                                : row.priceSource === 'unpriced'
                                                  ? 'No selling price yet — this line is NOT billed until you set one (the supplier cost is never billed for this service)'
                                                  : 'Billing at cost — click to set a selling price (default for all clients, or tick "this client only")'
                                        }
                                      >
                                        {row.isPriced ? ZAR(row.unitPrice!) : <span className="xl-unpriced">unpriced</span>}
                                        {row.priceSource === 'client' && <span className="src-client" title="Client-specific price">👤</span>}
                                        {row.priceSource === 'default' && <span className="ovr-dot" />}
                                      </td>
                                    )}
                                    <td className="num">{ZAR(row.amount || '0')}</td>
                                    <td className="num strong">{ZAR(row.monthly || '0')}</td>
                                    <td className="cycle">
                                      {row.cycle}
                                      {row.term && <div className="xl-term" title={row.renews ? `Renews ${row.renews}` : ''}>{row.term} term{row.renews ? ` · renews ${row.renews}` : ''}</div>}
                                    </td>
                                    <td className="ctr">{row.xeroLinked ? '✓' : ''}</td>
                                  </tr>
                                ) : (
                                  <tr key={i} className="xl-subtotal">
                                    <td className="svc"></td>
                                    <td colSpan={6}>{SERVICE_LABELS[row.service] || row.service} subtotal · {row.itemCount} item{row.itemCount !== 1 ? 's' : ''}</td>
                                    <td className="num strong">{ZAR(row.subtotal || '0')}</td>
                                    <td colSpan={2}></td>
                                  </tr>
                                ))}
                                <tr className="xl-total">
                                  <td className="svc"></td>
                                  <td colSpan={6}>Total billable / month</td>
                                  <td className="num">{ZAR(customerDetails.monthly_total || '0')}</td>
                                  <td colSpan={2}></td>
                                </tr>
                                {customerDetails.monthly_cost_total && parseFloat(customerDetails.monthly_cost_total) > 0 && (
                                  <tr className="xl-total cost-row">
                                    <td className="svc"></td>
                                    <td colSpan={6}>
                                      Cost / month · margin {ZAR(customerDetails.monthly_margin || '0')}
                                      {parseFloat(customerDetails.monthly_total) > 0 &&
                                        ` (${((parseFloat(customerDetails.monthly_margin || '0') / parseFloat(customerDetails.monthly_total)) * 100).toFixed(1)}%)`}
                                    </td>
                                    <td className="num">{ZAR(customerDetails.monthly_cost_total)}</td>
                                    <td colSpan={2}></td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {snapshotMode && run ? (
                              <p className="xl-hint">
                                <strong>Quantities</strong> were frozen on {formatDate(run.snapshot_at)} as the {run.period_label} snapshot —
                                later syncs do not change them{!run.locked && ' (Re-run snapshot to re-freeze)'}. <strong>Prices are live</strong>:
                                click a Sell cell to set the default or 👤 client price as usual; the totals here, on the Integrations
                                billing status and in the Halo push follow the price book.
                              </p>
                            ) : (
                            <p className="xl-hint">
                              <strong>Cost</strong> is synced from the integration (CloudBlue = our buy price).
                              Click a <strong>Sell</strong> cell to set the <strong>default selling price</strong> (all
                              clients) or tick <strong>👤 this client only</strong> for a client-specific price.
                              Resolution: client price → default price → cost. Orange dot = default, 👤 = client price.
                              Manage everything (incl. Halo item mapping) in the Price book tab on the Integrations page.
                            </p>
                            )}
                            </>
                          );
                        })()}

                        {/* M365 ↔ CloudBlue quantity check — same dense grid */}
                        {customerDetails.m365_reconciliation && customerDetails.m365_reconciliation.status !== 'no_m365' && (
                          <div className="m365-recon">
                            <h4>
                              M365 ↔ CloudBlue quantity check
                              <span className={`m365-pill ${customerDetails.m365_reconciliation.status}`}>
                                {customerDetails.m365_reconciliation.status === 'match' ? 'All match' : `${customerDetails.m365_reconciliation.mismatch_lines} mismatch`}
                              </span>
                            </h4>
                            <table className="xl-grid m365-recon-table">
                              <thead>
                                <tr><th>Product</th><th className="num">M365 Qty</th><th className="num">CloudBlue Qty</th><th className="num">Diff</th><th>Status</th></tr>
                              </thead>
                              <tbody>
                                {customerDetails.m365_reconciliation.lines.map((l: any, i: number) => (
                                  <tr key={i} className={`recon-${l.status}`}>
                                    <td>{l.product_name}</td>
                                    <td className="num">{l.m365_qty}</td>
                                    <td className="num">{l.cloudblue_qty}</td>
                                    <td className={`num ${l.m365_qty - l.cloudblue_qty !== 0 ? 'strong' : ''}`}>
                                      {l.m365_qty - l.cloudblue_qty === 0 ? '' : (l.m365_qty - l.cloudblue_qty > 0 ? `+${l.m365_qty - l.cloudblue_qty}` : l.m365_qty - l.cloudblue_qty)}
                                    </td>
                                    <td>{reconLabel(l.status)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="m365-note">Microsoft 365 is a verification check only — it is <strong>not billed</strong>. CloudBlue is the billed source. Positive diff = provisioned more than billed (under-billing).</p>
                          </div>
                        )}

                        {customer.missing_services.length > 0 && (
                          <div className="missing-services">
                            <strong>Issues:</strong> {customer.missing_services.join(', ')}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {visibleCustomers.length === 0 && !showHidden && (
          <div className="empty-state">
            <p>No customers match the current filter.</p>
            {hiddenCount > 0 && (
              <p className="empty-hint">{hiddenCount} customer{hiddenCount !== 1 ? 's are' : ' is'} hidden. <button className="link-btn" onClick={() => setShowHidden(true)}>Show hidden</button></p>
            )}
          </div>
        )}
        {visibleCustomers.length === 0 && showHidden && (
          <div className="empty-state">
            <p>No hidden customers.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Billing;
